import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ConsentTypes } from '@credit-acceptance/shareable-utilities';
import { FullStory } from '@fullstory/browser';
import { adobeAnalytics } from 'adobe-analytics';
import { catchError, map, take, takeUntil, tap } from 'rxjs';

import { PREQUAL_JSON } from '../../assets/constants';
import { EFeatureFlag, FeatureFlagService } from '../feature-flag.service';
import { ConsentTextInfoRestService } from '../services/consent-test-info-rest.service';
import { PrequalService } from '../services/prequal.service';
import { RuntimeConfigService } from '../services/runtime-config';
import { ScrollingService } from '../services/scrolling.service';
import {
  DealerApproval,
  SubmitCapsLeadRestService,
} from '../services/submit-caps-lead-rest.service';
import { Unsubscribe } from '../shared/classes/unsubscribe.class';
import { FS_EVENTS, FS_LABELS } from '../shared/constants/fsevents';
import { DealerLocation } from '../shared/models/prequal.types';
import {
  accessToken,
  cacChubSSNId,
  monthlyPayment,
  portalApplicationId,
} from '../shared/state/local-cache';
import { isWwwAiChatChallenger } from '../shared/utils/ab-test';
import { getSelectedDealerCountFromUrl } from '../shared/utils/selected.dealers';

declare const FS: typeof FullStory;

@Component({
  selector: 'capq-success-prequal',
  templateUrl: './success-prequal.component.html',
  styleUrls: ['./success-prequal.component.scss'],
  standalone: false,
})
export class SuccessPrequalComponent
  extends Unsubscribe
  implements OnInit, AfterViewInit, OnDestroy
{
  /** Default max wait (ms) before the success page renders if the env var is unset. */
  private readonly defaultMaxDelayMs = 10000;
  /** LaunchDarkly flag gating the inventory-module embed on the confirmation page. */
  private readonly inventoryModuleFlag = 'enabled-inventory-module-embed';

  @Input() immediateEmit = false;
  @Input() isPrequal = false;
  @Input() adobeScriptLoaded = false;
  @Input() submissionFailureReason = '';
  @Input() smsConsentGiven = false;
  @Input() shouldLoadChatWidget = false;
  @Output() showFinalSuccessDealerWebsite = new EventEmitter<boolean>();

  @ViewChild('topElement') topElement!: ElementRef<HTMLDivElement>;

  onImageLoad() {
    this.isImageLoaded = true;
  }

  isImageLoaded = false;
  selectedDealerCount = 0;

  zipCode = 48034;
  models: string[] = [];
  monthlyPayment = 0;
  logoURL = 'assets/images/CA_Logo.svg';
  prequalImg = 'assets/images/pre-qual.png';
  dealers!: DealerLocation[];
  osmSSNConsentContent = '';
  isUserAgreed = false;
  desktopView: boolean | undefined;
  finalResponse = false;
  saveMeCounter = 60;
  formFieldData: any;
  consentCreditAuthVersion: any;
  consentversionid = '';
  globalText: any;
  timeIntervalId: any;
  initialTimeoutId!: ReturnType<typeof setTimeout>;
  ttMETAstarttime: number | null = null;
  isWaiting = false;
  residenceData: any = {};
  userSelectedDealersCount = 0;
  leadCreated = false;

  /** Per-dealer approval numbers (lead IDs) from the caps-lead response; threaded to the embed. */
  dealerApprovals: DealerApproval[] = [];
  /** Whether the inventory-module embed is enabled (drives the bounded render wait). */
  inventoryModuleEnabled = false;
  /** Whether the post-prequal chatbot widget is enabled via LaunchDarkly. */
  chatWidgetEnabled = false;

  /**
   * SVRS-49895: only the WWW AI Chat challenger group is shown the on-site AI
   * chatbot; the control group gets SMS consent instead.
   */
  get showWwwAiChatbot(): boolean {
    return isWwwAiChatChallenger();
  }
  /** Timer that caps how long we wait for the caps-lead response before rendering. */
  private renderTimeoutId?: ReturnType<typeof setTimeout>;

  constructor(
    private prequalService: PrequalService,
    private readonly scrollingService: ScrollingService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly submitcapsLeadRestService: SubmitCapsLeadRestService,
    private readonly consentTextInfoRestService: ConsentTextInfoRestService,
    private readonly runtimeConfigService: RuntimeConfigService
  ) {
    super();
    this.monthlyPayment = monthlyPayment();
  }

  ngOnInit(): void {
    this.ttMETAstarttime = new Date().getTime();
    this.formFieldData = PREQUAL_JSON?.success;

    this.dealers = this.prequalService.getDealerLocations() ?? [];

    // Set userSelectedDealersCount based on the isDealerSelected value to true
    this.userSelectedDealersCount = this.dealers.filter(
      (dealer) => dealer.isDealerSelected
    ).length;

    this.residenceData = this.prequalService.getResidenceData() ?? {};

    this.consentTextInfoRestService
      .getConsentByType(ConsentTypes.OSM_SSN)
      .pipe(
        tap((resp) => {
          const accessTokenRef = resp.headers?.get('access-token');
          if (accessTokenRef) accessToken(accessTokenRef);

          const chubSSNid = resp.headers?.get('cac-chub-ssid');
          if (chubSSNid) cacChubSSNId(chubSSNid);
        }),
        map((result) => result.body?.getConsentByType),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((data) => {
        if (data) {
          this.osmSSNConsentContent = data.consenttext;
          this.consentCreditAuthVersion = data.consenttextid;
          this.consentversionid = data.consentversion;
        }
      });

    this.featureFlagService
      .isEnabled('enabled-select-dealers-prequalification')
      .pipe(
        take(1),
        tap((isEnabled) => {
          this.selectedDealerCount = isEnabled
            ? getSelectedDealerCountFromUrl()
            : 0;
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    this.featureFlagService
      .isEnabled(this.inventoryModuleFlag)
      .pipe(
        take(1),
        tap((isEnabled) => (this.inventoryModuleEnabled = isEnabled)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    this.featureFlagService
      .isEnabled(EFeatureFlag.CHATBOT_WIDGET_ENABLED)
      .pipe(
        take(1),
        tap((isEnabled) => (this.chatWidgetEnabled = isEnabled)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    this.initialTimeoutId = setTimeout(() => {
      this.startTimer();
    }, 180000);

    this.adobeScriptLoaded &&
      adobeAnalytics.emitEvent('cac-page-view', null, {
        form_design: 'v 1.0',
        section_name: 'prequalified',
        form_submission_id: (portalApplicationId() ?? '').toString(),
        dealer_list: '',
        form_submission_status: 'success',
        form_failure_reason: this.submissionFailureReason
          ? this.submissionFailureReason
          : undefined,
        event_type: 'cac-page-view',
      });
  }

  ngAfterViewInit(): void {
    this.scrollingService.scrollToTop(this.topElement.nativeElement);
  }

  startTimer() {
    this.timeIntervalId = setInterval(() => {
      this.saveMeCounter = this.saveMeCounter - 1;
      if (this.saveMeCounter == 0) {
        clearInterval(this.saveMeCounter);
        document.location.href = document.location.origin;
      }
    }, 1000);
  }

  stopTimer() {
    clearInterval(this.timeIntervalId);
    clearInterval(this.initialTimeoutId);
  }

  handleSubmit() {
    this.isWaiting = true;
    let dealer_list = '';
    this.dealers.forEach((dealer, ind) => {
      dealer_list += `${dealer.dealerId}|${dealer.dealerName}`;
      if (ind < 2) {
        dealer_list += ', ';
      }
    });
    this.adobeScriptLoaded &&
      adobeAnalytics.emitEvent('cac-save-me-time', null, {
        form_design: 'v 1.0',
        section_name: '',
        form_submission_id: (portalApplicationId() ?? '').toString(),
        dealer_list,
        form_submission_status: 'success',
        event_type: 'cac-save-me-time',
      });

    this.submitcapsLeadRestService
      .submitCapsLead({
        agreementConsent: this.isUserAgreed,
        ...(this.smsConsentGiven && { smsConsent: true }),
      })
      .pipe(
        tap((res) => {
          this.leadCreated = !!res?.leadCreated;
          this.dealerApprovals = res?.dealerApprovals ?? [];
          this.onCapsLeadResolved();
        }),
        catchError(() => {
          this.prequalService.triggerErrorScreen(true);
          throw new Error();
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    if (!(typeof FS === 'undefined')) {
      FS.event(FS_EVENTS.OSM_HOT_LEAD, {
        action_label: FS_LABELS.SAVE_ME_TIME,
        osmHotLeadSubmitted: true,
      });
    }

    // When the embed is enabled, delay rendering the success page until the
    // caps-lead response (carrying per-dealer lead IDs) resolves — but never
    // longer than the configurable max timeout. When disabled, render now.
    if (this.inventoryModuleEnabled) {
      const maxDelay =
        this.runtimeConfigService.prequalConfig?.[
          'FINAL_SUCCESS_PAGE_LOAD_MAX_DELAY'
        ] ?? this.defaultMaxDelayMs;
      this.renderTimeoutId = setTimeout(
        () => this.showFinalResponse(),
        maxDelay
      );
    } else {
      this.showFinalResponse();
    }
  }

  /**
   * Caps-lead response arrived. Stop waiting, hydrate the embed, and render the
   * success page if it has not rendered yet. If the page already rendered
   * (max timeout elapsed first), the new `dealerApprovals` reference flows to
   * `FinalSuccessResponseComponent`, which injects the late lead IDs via
   * `setOptions()`.
   */
  private onCapsLeadResolved(): void {
    if (this.renderTimeoutId) {
      clearTimeout(this.renderTimeoutId);
      this.renderTimeoutId = undefined;
    }
    this.showFinalResponse();
  }

  /** Swaps in the final-success view. Idempotent — safe to call more than once. */
  private showFinalResponse(): void {
    this.isWaiting = false;
    if (this.finalResponse) {
      return;
    }
    this.finalResponse = true;
    if (this.dealers?.length) {
      this.showFinalSuccessDealerWebsite.emit(true);
    }
    this.stopTimer();
  }

  override ngOnDestroy(): void {
    clearTimeout(this.renderTimeoutId);
    super.ngOnDestroy();
  }

  getDealerFinderUrl(): string {
    const baseUrl = '/car-buyers/dealer-finder';
    const city = this.residenceData?.city;
    const state = this.residenceData?.userState;
    if (state && city) {
      return `${baseUrl}/${state}-${city}`;
    }
    return baseUrl;
  }

  onCheckboxChange(e: any) {
    if (e.target.checked) {
      this.isUserAgreed = true;
    } else {
      this.isUserAgreed = false;
    }
  }
}
